import type { ReactNode } from "react";
import { Dialog, Fade, IconButton } from "@mui/material";
import type { Breakpoint } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

export interface DialogShellProps {
  open: boolean;
  /** Dismiss intent (corner X, backdrop, Escape). Ignored entirely under `blocking`. */
  onClose: () => void;
  /** Disable every dismissal affordance without unmounting (e.g. while a confirm is in flight). */
  closeDisabled?: boolean;
  /** No Escape, no backdrop close, no corner X — for must-acknowledge dialogs. */
  blocking?: boolean;
  /** Defaults to true. Ignored under `blocking` — a must-acknowledge dialog
   *  never renders a dismissing X, whatever the caller passes. */
  showCloseButton?: boolean;
  /**
   * Compact px cap for kit-style confirm/sign dialogs (paper also gets a
   * 380px floor). Ignored when `size` is given.
   */
  maxWidth?: number;
  /**
   * Breakpoint sizing for content dialogs (forms, previews, wizards):
   * full-width paper at the given breakpoint, natural MUI scroll behavior,
   * no px floor so small viewports stay usable.
   */
  size?: Breakpoint;
  /** Fullscreen paper (responsive wizards on small viewports). */
  fullScreen?: boolean;
  /**
   * Swallow backdrop clicks while keeping Escape and the corner X live —
   * for dialogs where a stray click must not even raise a discard prompt.
   */
  ignoreBackdropClick?: boolean;
  /** Merged onto the paper after the shell's own styles (heights, overflow, layout). */
  paperSx?: SxProps<Theme>;
  titleId: string;
  descriptionId?: string;
  children: ReactNode;
}

/**
 * Internal chrome shared by every app dialog: bordered elevation-0 paper,
 * blurred backdrop, 200ms fade, corner X, aria wiring. Paper radius comes
 * from the theme's MuiDialog styleOverride — do not set it here.
 */
export function DialogShell({
  open,
  onClose,
  closeDisabled = false,
  blocking = false,
  showCloseButton,
  maxWidth = 420,
  size,
  fullScreen = false,
  ignoreBackdropClick = false,
  paperSx,
  titleId,
  descriptionId,
  children,
}: DialogShellProps) {
  const dismissable = !blocking && !closeDisabled;
  const handleDialogClose = (_event: object, reason: string) => {
    if (ignoreBackdropClick && reason === "backdropClick") return;
    onClose();
  };

  // fullScreen joins the plain-border branch: the compact 380px floor and
  // 420px cap must never constrain a fullscreen paper on a narrow viewport.
  const baseSx: SxProps<Theme> = size || fullScreen
    ? {
        border: "1px solid",
        borderColor: "divider",
      }
    : {
        minWidth: 380,
        maxWidth,
        overflow: "visible",
        border: "1px solid",
        borderColor: "divider",
      };
  // Array composition preserves caller sx whether it's an object, a function,
  // or an array — spreading with `as object` silently drops the other forms.
  const mergedSx: SxProps<Theme> = !paperSx
    ? baseSx
    : Array.isArray(paperSx)
      ? [baseSx, ...paperSx]
      : [baseSx, paperSx];

  return (
    <Dialog
      open={open}
      onClose={dismissable ? handleDialogClose : undefined}
      disableEscapeKeyDown={!dismissable}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      maxWidth={size ?? false}
      fullWidth={Boolean(size)}
      fullScreen={fullScreen}
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: mergedSx,
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      {!blocking && (showCloseButton ?? true) && (
        <IconButton
          onClick={onClose}
          disabled={closeDisabled}
          size="small"
          aria-label="Close"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            zIndex: 1,
            color: "text.secondary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      {children}
    </Dialog>
  );
}

/**
 * Consistent sx for primary/secondary action buttons inside dialog footers.
 * (Moved verbatim from the retired components/ui/StyledDialog.)
 */
export const dialogActionButtonSx: SxProps<Theme> = {
  py: 1.25,
  borderRadius: 2,
  textTransform: "none",
  fontSize: "0.95rem",
  fontWeight: 600,
};
